import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Ship, AlertTriangle, MapPin, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const RedSeaUpdateArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-blue-500 text-white">
                <Ship className="h-3 w-3 mr-1" />
                {t('article.redseaupdate.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.redseaupdate.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.redseaupdate.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.redseaupdate.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://shippingtelegraph.com/wp-content/uploads/2025/01/HAPAG-GEMINI.png"
              alt="Container ships and Red Sea shipping route challenges" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Ship className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.redseaupdate.allianceLabel')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('article.redseaupdate.allianceValue')}</div>
                <p className="text-sm text-gray-600">{t('article.redseaupdate.allianceDesc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.redseaupdate.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-red-600">{t('article.redseaupdate.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.redseaupdate.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-gray-600" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-gray-600">No Date</div>
                <p className="text-sm text-gray-600">Specific timeline</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.redseaupdate.overviewTitle')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.redseaupdate.overviewContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.redseaupdate.positionTitle')}</h3>
            <blockquote className="border-l-4 border-blue-500 pl-6 italic text-gray-700 mb-6">
              "{t('article.redseaupdate.quoteContent')}"
              <footer className="text-sm font-semibold mt-2">{t('article.redseaupdate.quoteFooter')}</footer>
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.redseaupdate.efficiencyTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.redseaupdate.efficiencyContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.redseaupdate.visitTitle')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.redseaupdate.developmentsTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.redseaupdate.partnership').split(':')[0]}:</strong> {t('article.redseaupdate.partnership').split(':')[1]}</li>
                <li><strong>{t('article.redseaupdate.preliminaryReturn').split(':')[0]}:</strong> {t('article.redseaupdate.preliminaryReturn').split(':')[1]}</li>
                <li><strong>{t('article.redseaupdate.precursorPhase').split(':')[0]}:</strong> {t('article.redseaupdate.precursorPhase').split(':')[1]}</li>
                <li><strong>{t('article.redseaupdate.authorityCollab').split(':')[0]}:</strong> {t('article.redseaupdate.authorityCollab').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.redseaupdate.gazaTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.redseaupdate.gazaContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry-Wide Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Gemini alliance's cautious approach reflects broader industry sentiment:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Safety Priority:</strong> Crew and cargo security takes precedence over efficiency</li>
              <li><strong>Insurance Considerations:</strong> Risk assessment affects coverage and costs</li>
              <li><strong>Customer Expectations:</strong> Reliable service delivery despite longer routes</li>
              <li><strong>Competitive Positioning:</strong> Coordinated approach with alliance partner</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Alternative Route Impact</h3>
            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Cape of Good Hope Route Consequences:</h4>
              <ul className="space-y-2">
                <li><strong>Extended Transit Times:</strong> Additional 10-14 days for Asia-Europe routes</li>
                <li><strong>Increased Fuel Costs:</strong> Longer distances require more bunker fuel</li>
                <li><strong>Capacity Constraints:</strong> More vessels needed to maintain service frequency</li>
                <li><strong>Environmental Impact:</strong> Higher emissions due to extended routing</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Speculation Response</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The joint statement was specifically issued to address media speculation about resuming 
              Gemini sailings through the Suez Canal. Both companies emphasized that no confirmed 
              schedule exists for reinstating East-West transits via the Red Sea, despite ongoing 
              discussions and preliminary agreements with canal authorities.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Patience</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The alliance's approach demonstrates strategic patience in balancing operational efficiency 
              with risk management. While competitors may take different approaches, Maersk and Hapag-Lloyd 
              are maintaining their conservative stance until security conditions show sustained improvement.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Future Monitoring</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The carriers continue to assess the situation through multiple channels, including 
              diplomatic developments, security intelligence, and industry coordination. Any decision 
              to resume Red Sea transits will be based on comprehensive risk assessment rather than 
              competitive pressure or operational convenience.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on official Gemini alliance statement reported by Kuehne+Nagel maritime news. 
                <a href="https://mykn.kuehne-nagel.com/news/article/no-specific-timing-to-sailing-through-the-red" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default RedSeaUpdateArticle;